import { FC, useEffect, useRef, useState } from 'react';
import { IonTextarea, IonButton } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { BeeChatState } from './BeeChatState';
import { IChatMessage } from './BeeChatClient';
import { MarkdownMessage } from './MarkdownMessage';
import { PaperPlaneIcon } from '../theme/Icons';
import { UserState } from '../user/UserState';
import dayjs from 'dayjs';

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid var(--ion-color-step-100)'
    },

    beaAvatar: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        objectFit: 'cover'
    },

    headerInfo: {
        display: 'flex',
        flexDirection: 'column'
    },

    beaName: {
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--ion-color-dark)'
    },

    onlineStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 14,
        color: 'var(--ion-color-medium)'
    },

    onlineDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#4CAF50'
    },

    messagesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: 16
    },

    messageWrapper: {
        display: 'flex',
        marginBottom: 16,
        alignItems: 'flex-end',
        gap: 8
    },

    userMessageWrapper: {
        justifyContent: 'flex-end'
    },

    userMessageContent: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        flex: 1
    },

    assistantMessageWrapper: {
        justifyContent: 'flex-start'
    },

    smallAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0
    },

    userAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: 'var(--ion-color-primary)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 600,
        flexShrink: 0
    },

    messageBubble: {
        maxWidth: 'calc(100% - 15px)',
        padding: '12px 16px',
        borderRadius: 16,
        fontSize: 15,
        lineHeight: 1.5,
        wordBreak: 'break-word'
    },

    userBubble: {
        backgroundColor: 'var(--ion-color-primary)',
        color: 'white',
        borderBottomRightRadius: 4
    },

    assistantBubble: {
        backgroundColor: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-dark)',
        borderBottomLeftRadius: 4
    },

    timestamp: {
        fontSize: 11,
        color: 'var(--ion-color-medium)',
        marginTop: 4,
        width: '100%'
    },

    userTimestamp: {
        textAlign: 'right'
    },

    assistantTimestamp: {
        textAlign: 'left'
    },

    typingIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        marginBottom: 16
    },

    typingDots: {
        display: 'flex',
        gap: 4,
        padding: '12px 16px',
        backgroundColor: 'var(--ion-color-step-100)',
        borderRadius: 16,

        '& span': {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'var(--ion-color-medium)',
            animation: '$bounce 1.4s infinite ease-in-out both',

            '&:nth-child(1)': {
                animationDelay: '-0.32s'
            },
            '&:nth-child(2)': {
                animationDelay: '-0.16s'
            }
        }
    },

    '@keyframes bounce': {
        '0%, 80%, 100%': {
            transform: 'scale(0)'
        },
        '40%': {
            transform: 'scale(1)'
        }
    },

    inputArea: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 16px',
        borderTop: '1px solid var(--ion-color-step-100)',
        backgroundColor: 'white',
        marginBottom: 'var(--ion-safe-area-bottom, 0px)'
    },

    textareaWrapper: {
        flex: 1,
        maxHeight: 150,
        overflowY: 'auto'
    },

    chatTextarea: {
        '--border-radius': '12px !important',
        '--background': 'var(--ion-color-step-50)',
        '--border-width': '0px !important',
        minHeight: 'unset !important',
        margin: 0,

        '& textarea': {
            paddingTop: '15px !important',
            paddingBottom: '15px !important',
            paddingInlineStart: 16,
            paddingInlineEnd: 16
        }
    },

    sendButton: {
        '--border-radius': '50%',
        height: 44,
        minHeight: 44,
        width: 44,
        margin: 0,

        '&::part(native)': {
            borderRadius: '50%',
            padding: 0
        },

        '& ion-text': {
            marginLeft: -2
        }
    }
});

interface ActiveChatViewProps {
    'data-id'?: string;
}

export const ActiveChatView: FC<ActiveChatViewProps> = ({ 'data-id': dataId }) => {
    const classes = useStyles();
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLIonTextareaElement>(null);
    const [inputValue, setInputValue] = useState('');

    const chatState = useInstance(BeeChatState);
    useSubscription(() => chatState);

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const { messages, isSending, isStreaming, streamingText } = chatState.state;

    // We want to scroll when a new user message is added, and when the first chunk is streamed back,
    // but allow the user to control scrolling as streaming continues. The variable isAboutToStream
    // toggles to true when the user message is submitted, and back to false when the first chunk arrives.
    // At all other times it is false. So this causes the useEffect to trigger on the two occasions we want.
    const isAboutToStream = isStreaming && !streamingText.length;
    useEffect(() => {
        scrollToLastMessage();
    }, [isAboutToStream]);

    // Scroll to bottom when switching conversations (activeChat changes)
    const activeChat = chatState.state.activeChat;
    useEffect(() => {
        scrollToBottom();
    }, [activeChat]);

    const scrollToLastMessage = () => {
        // Small delay to let DOM render before scrolling
        setTimeout(() => {
            const element = lastMessageRef.current;
            const container = messagesContainerRef.current;
            if (element && container) {
                // Calculate element position relative to container using getBoundingClientRect
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
                const scrollPosition = relativeTop - 16; // 16px padding above
                container.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'smooth' });
            }
        }, 50);
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            const container = messagesContainerRef.current;
            if (container) {
                container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
            }
        }, 50);
    };

    const handleSend = async () => {
        const text = inputValue.trim();
        if (!text || isSending) return;

        setInputValue('');
        await chatState.sendMessage(text);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const getUserInitial = (): string => {
        const firstName = userState.state.firstName;
        if (firstName) {
            return firstName.charAt(0).toUpperCase();
        }
        return 'U';
    };

    const getUserPictureUrl = (): string | undefined => {
        return userState.state.pictureUrl;
    };

    const formatTimestamp = (createdAt: string): string => {
        const date = dayjs(createdAt);
        const now = dayjs();

        if (date.isSame(now, 'day')) {
            return date.format('[Today at] h:mma');
        }
        if (date.isSame(now.subtract(1, 'day'), 'day')) {
            return date.format('[Yesterday at] h:mma');
        }
        if (date.isSame(now, 'year')) {
            return date.format('MMM D [at] h:mma');
        }
        return date.format('MMM D, YYYY [at] h:mma');
    };


    const renderMessage = (message: IChatMessage, index: number) => {
        const isUser = message.role === 'user';
        const isLastMessage = index === messages.length - 1;

        return (
            <div
                key={index}
                ref={isLastMessage ? lastMessageRef : undefined}
                className={`${classes.messageWrapper} ${isUser ? classes.userMessageWrapper : classes.assistantMessageWrapper}`}
                data-id={`bea-chat-message-${message.role}-${index}`}
            >
                {!isUser && (
                    <img
                        src="/assets/images/help/helper-bea.png"
                        alt="Bea"
                        className={classes.smallAvatar}
                    />
                )}

                <div className={isUser ? classes.userMessageContent : undefined}>
                    <div
                        className={`${classes.messageBubble} ${isUser ? classes.userBubble : classes.assistantBubble}`}
                    >
                        {isUser ? message.content : <MarkdownMessage content={message.content} />}
                    </div>
                    <div className={`${classes.timestamp} ${isUser ? classes.userTimestamp : classes.assistantTimestamp}`}>
                        {formatTimestamp(message.createdAt)}
                    </div>
                </div>

                {isUser && (
                    getUserPictureUrl() ? (
                        <img
                            src={getUserPictureUrl()}
                            alt="You"
                            className={classes.smallAvatar}
                        />
                    ) : (
                        <div className={classes.userAvatar}>
                            {getUserInitial()}
                        </div>
                    )
                )}
            </div>
        );
    };

    return (
        <div className={classes.container} data-id={dataId || 'bea-chat-view'}>
            <div className={classes.header}>
                <img
                    src="/assets/images/help/helper-bea.png"
                    alt="Bea"
                    className={classes.beaAvatar}
                />
                <div className={classes.headerInfo}>
                    <span className={classes.beaName}>Bea</span>
                    <span className={classes.onlineStatus}>
                        <span className={classes.onlineDot} />
                        Online
                    </span>
                </div>
            </div>

            <div ref={messagesContainerRef} className={classes.messagesContainer} data-id="bea-chat-messages">
                {messages.map((message, index) => renderMessage(message, index))}

                {isStreaming && streamingText && (
                    <div
                        className={`${classes.messageWrapper} ${classes.assistantMessageWrapper}`}
                        data-id="bea-chat-streaming-message"
                    >
                        <img
                            src="/assets/images/help/helper-bea.png"
                            alt="Bea"
                            className={classes.smallAvatar}
                        />
                        <div>
                            <div
                                className={`${classes.messageBubble} ${classes.assistantBubble}`}
                            >
                                <MarkdownMessage content={streamingText} />
                            </div>
                            <div className={`${classes.timestamp} ${classes.assistantTimestamp}`}>
                                &nbsp;
                            </div>
                        </div>
                    </div>
                )}

                {(isSending || (isStreaming && !streamingText)) && (
                    <div className={classes.typingIndicator} data-id="bea-chat-loading">
                        <img
                            src="/assets/images/help/helper-bea.png"
                            alt="Bea"
                            className={classes.smallAvatar}
                        />
                        <div className={classes.typingDots}>
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                )}
            </div>

            <div className={classes.inputArea}>
                <div className={classes.textareaWrapper}>
                    <IonTextarea
                        ref={textareaRef}
                        className={classes.chatTextarea}
                        value={inputValue}
                        placeholder="Type a message"
                        fill="outline"
                        autoGrow
                        rows={1}
                        data-id="bea-chat-input"
                        onIonInput={(e) => setInputValue(e.detail.value || '')}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                <IonButton
                    color="secondary"
                    className={classes.sendButton}
                    disabled={!inputValue.trim() || isSending}
                    onClick={handleSend}
                    data-id="bea-chat-send-button"
                >
                    <PaperPlaneIcon color="light" />
                </IonButton>
            </div>
        </div>
    );
};
